import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Filter, 
  MoreVertical,
  Check
} from 'lucide-react';
import { cn } from '../utils/cn';

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (value: any, item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

export interface Action<T> {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: (item: T) => void;
  variant?: 'default' | 'danger' | 'success';
  condition?: (item: T) => boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  actions?: Action<T>[];
  searchable?: boolean;
  filterable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  onRowClick?: (item: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  selectable?: boolean;
  selectedItems?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  idField?: keyof T;
}

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  actions = [],
  searchable = true,
  filterable = false,
  pagination = true,
  pageSize = 10,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  idField = 'id' as keyof T
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showActions, setShowActions] = useState<{ [key: string]: boolean }>({});

  // Filter data based on search term
  const filteredData = data.filter(item =>
    columns.some(column => {
      const key = String(column.key);
      const value = key.includes('.') 
        ? key.split('.').reduce((obj: any, key: string) => obj?.[key], item)
        : item[column.key];
      return String(value).toLowerCase().includes(searchTerm.toLowerCase());
    })
  );

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig) return 0;

    const aValue = sortConfig.key.includes('.')
      ? sortConfig.key.split('.').reduce((obj: any, key: string) => obj?.[key], a)
      : a[sortConfig.key];
    const bValue = sortConfig.key.includes('.')
      ? sortConfig.key.split('.').reduce((obj: any, key: string) => obj?.[key], b)
      : b[sortConfig.key];

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate data
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = pagination 
    ? sortedData.slice(startIndex, startIndex + pageSize)
    : sortedData;

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleActions = (itemId: string) => {
    setShowActions(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getActionVariantStyles = (variant: string) => {
    switch (variant) {
      case 'danger':
        return 'text-red-600 hover:text-red-700 hover:bg-red-50';
      case 'success':
        return 'text-green-600 hover:text-green-700 hover:bg-green-50';
      default:
        return 'text-gray-600 hover:text-gray-700 hover:bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("bg-white rounded-lg border border-gray-200", className)}>
      {/* Header */}
      {(searchable || filterable) && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {searchable && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                />
              </div>
            )}
            {filterable && (
              <button className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <Filter className="w-4 h-4" />
                <span>Filter</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto overflow-y-visible relative">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {selectable && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <div className="relative inline-block">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onSelectionChange?.(paginatedData.map(item => String(item[idField])));
                        } else {
                          onSelectionChange?.([]);
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 checked:bg-blue-600 checked:border-blue-600 appearance-none relative"
                    />
                    {selectedItems.length === paginatedData.length && paginatedData.length > 0 && (
                      <Check className="absolute top-0 left-0 w-4 h-4 text-white pointer-events-none" strokeWidth={3} />
                    )}
                  </div>
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={cn(
                    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                    column.sortable && "cursor-pointer hover:bg-gray-100",
                    column.width
                  )}
                  onClick={() => column.sortable && handleSort(String(column.key))}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && sortConfig?.key === String(column.key) && (
                      <span className="text-[#667eea]">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {actions.length > 0 && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((item, index) => (
              <motion.tr
                key={item.id || index}
                className={cn(
                  "hover:bg-gray-50 transition-colors",
                  onRowClick && "cursor-pointer"
                )}
                onClick={() => onRowClick?.(item)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                {selectable && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="relative inline-block">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(String(item[idField]))}
                        onChange={(e) => {
                          e.stopPropagation();
                          const itemId = String(item[idField]);
                          if (e.target.checked) {
                            onSelectionChange?.([...selectedItems, itemId]);
                          } else {
                            onSelectionChange?.(selectedItems.filter(id => id !== itemId));
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 checked:bg-blue-600 checked:border-blue-600 appearance-none relative"
                      />
                      {selectedItems.includes(String(item[idField])) && (
                        <Check className="absolute top-0 left-0 w-4 h-4 text-white pointer-events-none" strokeWidth={3} />
                      )}
                    </div>
                  </td>
                )}
                {columns.map((column) => {
                  const key = String(column.key);
                  const value = key.includes('.') 
                    ? key.split('.').reduce((obj: any, key: string) => obj?.[key], item)
                    : item[column.key];
                  
                  return (
                    <td key={key} className="px-6 py-4 whitespace-nowrap">
                      {column.render ? column.render(value, item) : value}
                    </td>
                  );
                })}
                {actions.length > 0 && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActions(item.id || index.toString());
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {showActions[item.id || index.toString()] && (
                        <>
                          {/* Backdrop to close menu on outside click */}
                          <div 
                            className="fixed inset-0 z-[5]" 
                            onClick={() => toggleActions(item.id || index.toString())}
                          />
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className={cn(
                              "absolute right-0 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[20]",
                              // If it's the last row or there are few rows, position upward to avoid cutoff
                              index === paginatedData.length - 1 && paginatedData.length <= 3 ? "bottom-full mb-1" : "top-full mt-1"
                            )}
                          >
                          <div className="py-1">
                            {actions.map((action, actionIndex) => {
                              if (action.condition && !action.condition(item)) return null;
                              
                              return (
                                <button
                                  key={actionIndex}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(item);
                                    setShowActions(prev => ({ ...prev, [item.id || index.toString()]: false }));
                                  }}
                                  className={cn(
                                    "w-full flex items-center space-x-2 px-4 py-2 text-sm transition-colors",
                                    getActionVariantStyles(action.variant || 'default')
                                  )}
                                >
                                  {action.icon && <action.icon className="w-4 h-4" />}
                                  <span>{action.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                        </>
                      )}
                    </div>
                  </td>
                )}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {paginatedData.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-gray-600">{emptyMessage}</p>
        </div>
      )}

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(startIndex + pageSize, sortedData.length)} of {sortedData.length} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <div className="flex items-center space-x-1">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={cn(
                        "px-3 py-1 text-sm rounded",
                        currentPage === pageNum
                          ? "bg-[#667eea] text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
